import React from 'react';
import Reveal from './ui/Reveal';
import SectionLabel from './ui/SectionLabel';
import useParallax from '../hooks/useParallax';
import photo from '../lib/images';
import { mission } from '../data/content';

/**
 * "Not a handout. A hand up—and a walk together." The heart of the brief: the
 * contrast between standing in front of people and standing beside them.
 */
export default function Together() {
  const parallaxRef = useParallax({ strength: 0.14 });

  return (
    <section id="mission" className="on-dark grain relative isolate overflow-hidden bg-ink-900">
      <div ref={parallaxRef} className="absolute inset-0 -z-20 will-change-transform">
        <img
          {...photo('together-bg')}
          sizes="100vw"
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover object-left"
        />
      </div>
      <div
        className="absolute inset-0 -z-10 bg-ink-900/80 lg:bg-gradient-to-r lg:from-ink-900/40 lg:via-ink-900/88 lg:to-ink-900"
        aria-hidden="true"
      />

      <div className="shell relative grid py-24 sm:py-32 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <div className="hidden lg:block" aria-hidden="true" />
        <div>
          <Reveal variant="fade">
            <SectionLabel index="03" tone="light">
              Our Mission
            </SectionLabel>
          </Reveal>

          <Reveal
            as="h2"
            delay={80}
            className="mt-7 font-display text-[2rem] font-semibold uppercase leading-[1.06] tracking-[0.01em] text-white sm:text-4xl lg:text-[3rem]"
          >
            Not a handout.
            <span className="mt-1.5 block text-gold">A hand up—and a walk together.</span>
          </Reveal>
          <Reveal variant="rule" delay={240} className="rule mt-7" />

          <Reveal as="p" delay={300} className="mt-8 max-w-xl text-[0.98rem] leading-relaxed text-cream/75">
            {mission.lead}
          </Reveal>
          <Reveal as="p" delay={360} className="mt-4 max-w-xl text-[0.98rem] font-medium leading-relaxed text-cream">
            {mission.greater}
          </Reveal>

          {/* The brief sets these two lines against each other, so the layout does too. */}
          <div className="mt-10 grid gap-7 sm:grid-cols-2">
            <Reveal variant="left" delay={120} className="border-l-2 border-cream/15 pl-6">
              <p className="text-sm leading-relaxed text-cream/50">{mission.contrast.notLabel}</p>
              <p className="mt-2.5 font-serif text-2xl italic text-cream/45 line-through decoration-cream/25 decoration-1 sm:text-[1.75rem]">
                {mission.contrast.not}
              </p>
            </Reveal>
            <Reveal variant="right" delay={220} className="border-l-2 border-gold pl-6">
              <p className="text-sm leading-relaxed text-cream/70">{mission.contrast.doLabel}</p>
              <p className="mt-2.5 font-serif text-2xl font-medium italic text-gold sm:text-[1.75rem]">
                {mission.contrast.do}
              </p>
            </Reveal>
          </div>

          <Reveal delay={160} className="mt-10 max-w-xl space-y-2">
            {mission.body.map((line) => (
              <p key={line} className="text-[0.98rem] leading-relaxed text-cream/75">
                {line}
              </p>
            ))}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
